import { AssetIcon } from '@/components/AssetIcon';
import { CurveChart } from '@/components/formulas/CurveChart';
import { FormulaCard } from '@/components/formulas/FormulaCard';
import { GuideMenu } from '@/components/GuideMenu';
import { createFileRoute } from '@tanstack/react-router';
import React from 'react';

// Table component for consistent styling
const DataTable = ({ children }: { children: React.ReactNode }) => (
  <div className="overflow-x-auto">
    <table className="w-full border-collapse bg-white/[0.03] rounded-lg">
      {children}
    </table>
  </div>
);

const TableHeader = ({ children }: { children: React.ReactNode }) => (
  <th className="px-4 py-2 text-left text-[#e6c547] border-b border-white/10">
    {children}
  </th>
);

const TableCell = ({ children }: { children: React.ReactNode }) => (
  <td className="px-4 py-2 border-b border-white/10">{children}</td>
);

// Content sections
const IntroSection = () => (
  <section className="space-y-4">
    <h2 className="text-2xl font-bold text-[#e6c547]">Economics in Mythea</h2>
    <div className="space-y-4">
      <h3 className="text-xl font-semibold text-[#f0d060]">An Overview</h3>
      <p>
        Any wise ruler knows that the quickest path to power, fame, and fortune
        is
        <span className="text-green-400 font-semibold"> wealth</span>.
        Understanding how to expand your province and develop its infrastructure
        is key to building a{' '}
        <span className="text-green-400 font-semibold">strong economy</span>. If
        there is one fundamental aspect of Mythea you must grasp, it is this.
      </p>
      <p>
        The economic structure of Mythea will feel familiar to those from the
        world you hail from, featuring
        <span className="text-green-400 font-semibold">
          {' '}
          taxes, building construction, politics, and more
        </span>
        .
      </p>
    </div>
  </section>
);

const EconomicBasicsSection = () => (
  <section className="space-y-4">
    <h3 className="text-xl font-semibold text-[#f0d060]">Economic Basics</h3>
    <p>
      In Mythea, when infrastructure is built, it{' '}
      <span className="text-green-400 font-semibold">provides jobs</span> for
      your peasantry. Employed peasants{' '}
      <span className="text-green-400 font-semibold">pay taxes</span>, which in
      turn allow you to expand your province further. Your council of advisors
      will help you track much of this information, but here are some essential
      economic numbers:
    </p>
    <DataTable>
      <tbody>
        <tr>
          <TableHeader>Mythea Currency</TableHeader>
          <TableCell>Gold Coin (1gc or $1)</TableCell>
        </tr>
        <tr>
          <TableHeader>Income Per Employed Peasant</TableHeader>
          <TableCell>3gc per day</TableCell>
        </tr>
        <tr>
          <TableHeader>Income Per Unemployed Peasant</TableHeader>
          <TableCell>0.75gc per day</TableCell>
        </tr>
        <tr>
          <TableHeader>Peasant Work</TableHeader>
          <TableCell>1 Job</TableCell>
        </tr>
        <tr>
          <TableHeader>Jobs Created Per Building</TableHeader>
          <TableCell>25</TableCell>
        </tr>
        <tr>
          <TableHeader>Jobs Capacity for Max Efficiency</TableHeader>
          <TableCell>67% Filled</TableCell>
        </tr>
        <tr>
          <TableHeader>Prisoner Income</TableHeader>
          <TableCell>0.5gc per day</TableCell>
        </tr>
        <tr>
          <TableHeader>Prisoner Work</TableHeader>
          <TableCell>0.5 of a Job</TableCell>
        </tr>
      </tbody>
    </DataTable>
    <p>
      Prisoners, while a{' '}
      <span className="text-green-400 font-semibold">
        cheap source of labor
      </span>
      , will never match the efficiency of your peasants. They can contribute to
      your economy, but their true value lies on the battlefield, where they may
      fight for their freedom. Similarly, your{' '}
      <span className="text-green-400 font-semibold">
        buildings require workers
      </span>
      —if you lack enough peasants, your province&apos;s infrastructure will
      become less efficient. If this happens, you can{' '}
      <span className="text-green-400 font-semibold">
        invest in scientific advancements
      </span>{' '}
      or
      <span className="text-green-400 font-semibold">
        {' '}
        increase your peasant population
      </span>{' '}
      to restore efficiency.
    </p>
  </section>
);

const OverpopulationSection = () => (
  <section className="space-y-4">
    <h3 className="text-xl font-semibold text-[#f0d060]">
      Overpopulation Effects
    </h3>
    <p>
      If a province comes under sustained attack, it may become
      <span className="text-green-400 font-semibold"> overpopulated</span>,
      leading to various consequences. Overpopulation must be resolved before
      you can take certain actions.
    </p>
    <DataTable>
      <thead>
        <tr>
          <TableHeader>Province Status</TableHeader>
          <TableHeader>Consequence</TableHeader>
        </tr>
      </thead>
      <tbody>
        <tr>
          <TableCell>115% of Maximum Population</TableCell>
          <TableCell>
            Troops will desert each tick and refuse to go to battle
          </TableCell>
        </tr>
        <tr>
          <TableCell>120% of Maximum Population</TableCell>
          <TableCell>Thieves refuse to go on operations</TableCell>
        </tr>
        <tr>
          <TableCell>135% of Maximum Population</TableCell>
          <TableCell>Income is halved due to overpopulation</TableCell>
        </tr>
      </tbody>
    </DataTable>
  </section>
);

const NetWorthSection = () => (
  <section className="space-y-4">
    <h3 className="text-xl font-semibold text-[#f0d060]">Net Worth Values</h3>
    <p>
      Every asset in Mythea has a{' '}
      <span className="text-green-400 font-semibold">net worth value</span>,
      determining the overall power and influence of your province.
    </p>
    <DataTable>
      <thead>
        <tr>
          <TableHeader>Item</TableHeader>
          <TableHeader>Net Worth Value</TableHeader>
        </tr>
      </thead>
      <tbody>
        <tr>
          <TableCell>Peasants</TableCell>
          <TableCell>0.2</TableCell>
        </tr>
        <tr>
          <TableCell>Soldiers</TableCell>
          <TableCell>1.5</TableCell>
        </tr>
        <tr>
          <TableCell>Gold</TableCell>
          <TableCell>0.001</TableCell>
        </tr>
        <tr>
          <TableCell>Horses</TableCell>
          <TableCell>0.6</TableCell>
        </tr>
        <tr>
          <TableCell>Thieves</TableCell>
          <TableCell>4</TableCell>
        </tr>
        <tr>
          <TableCell>Wizards</TableCell>
          <TableCell>4</TableCell>
        </tr>
        <tr>
          <TableCell>Science Books</TableCell>
          <TableCell>0.02</TableCell>
        </tr>
        <tr>
          <TableCell>Barren Land</TableCell>
          <TableCell>20</TableCell>
        </tr>
        <tr>
          <TableCell>Buildings</TableCell>
          <TableCell>75</TableCell>
        </tr>
        <tr>
          <TableCell>Buildings in Construction</TableCell>
          <TableCell>40</TableCell>
        </tr>
      </tbody>
    </DataTable>
  </section>
);

function percentageBuildingEffect({
  landPercent,
  baseEffect,
  buildingEfficiency,
  scienceMod,
  maxEffect = 1,
}: {
  landPercent: number;
  baseEffect: number;
  buildingEfficiency: number;
  scienceMod: number;
  maxEffect?: number;
}) {
  const buildingPercent = Math.min(0.5, landPercent / 100);
  return Math.min(1, Math.min(Math.min(baseEffect * 0.25, maxEffect), baseEffect * buildingEfficiency * buildingPercent * (1 - buildingPercent) * scienceMod));
}

const percentageBuildingPoints = [0, 5, 10, 15, 20, 25, 30, 40, 50].map(percent => ({
  x: percent,
  y:
    percentageBuildingEffect({
      landPercent: percent,
      baseEffect: 1.5,
      buildingEfficiency: 1,
      scienceMod: 1,
    }) * 100,
}));

const BuildingFormulaSection = () => (
  <section className="space-y-4">
    <h3 className="text-xl font-semibold text-[#f0d060]">Percentage Building Formula</h3>
    <p>
      Most percentage-based buildings do not scale in a straight line. They use a diminishing-return curve based on the
      share of land assigned to the building, then apply Building Efficiency.
    </p>
    <div className="grid gap-4 lg:grid-cols-2">
      <FormulaCard
        title="Percentage Building Effect"
        formula="min(100%, min(base * 25%, base * BE * min(50%, building%) * (1 - min(50%, building%))))"
      >
        <p>
          The curve rises quickly at first, then flattens as more land is assigned to the same building. The standard
          cap for a percentage building is one quarter of its base effect.
        </p>
      </FormulaCard>
      <FormulaCard title="Training Grounds Example" formula="effect = 1.5 * BE * land% * (1 - land%)">
        <p>
          At 100% Building Efficiency and no extra modifiers, 20% Training Grounds gives about 24% offensive military
          efficiency. The curve approaches its cap near 50% land.
        </p>
      </FormulaCard>
    </div>
    <CurveChart
      title="Example Percentage Building Curve"
      description="Illustrative Training Grounds-style curve with base 1.5, BE 100%, no science modifier, and no age-specific building modifier."
      xLabel="Land assigned to one building"
      yLabel="Approx. effect"
      points={percentageBuildingPoints}
      formatX={value => `${Math.round(value)}%`}
      formatY={value => `${value.toFixed(1)}%`}
    />
  </section>
);

const BuildingSection = ({ buildings }: { buildings: any[] }) => {
  return (
    <section className="space-y-4">
      <h3 className="text-xl font-semibold text-[#f0d060]">
        Building Construction
      </h3>
      <p>
        Just as a{' '}
        <span className="text-green-400 font-semibold">
          blacksmith needs a forge
        </span>
        , your
        <span className="text-green-400 font-semibold">
          {' '}
          province needs buildings
        </span>{' '}
        to thrive. Land without buildings contributes little to your economy.
        Each structure serves a{' '}
        <span className="text-green-400 font-semibold">unique purpose</span>,
        either enhancing your strengths or covering your weaknesses.
      </p>
      <p>
        Building{' '}
        <span className="text-green-400 font-semibold">
          diversity is important
        </span>
        —focusing too heavily on a single type will reduce its overall
        effectiveness. There are{' '}
        <span className="text-green-400 font-semibold">
          three types of building effects
        </span>
        :
      </p>
      <ul className="list-disc list-inside space-y-2 pl-4">
        <li className="text-yellow-300">
          Capacity Buildings
          <span className="text-[#b8b8b8]">
            {' '}
            – Create space but are not impacted by efficiency.
          </span>
        </li>
        <li className="text-yellow-300">
          Flat Rate Buildings
          <span className="text-[#b8b8b8]">
            {' '}
            – Provide a fixed resource amount and require workers to be
            effective.
          </span>
        </li>
        <li className="text-yellow-300">
          Percentage-based Buildings
          <span className="text-[#b8b8b8]">
            {' '}
            – Their effect is based on the percentage of land dedicated to them,
            but efficiency decreases as more of the same type are built.
          </span>
        </li>
      </ul>

      <BuildingFormulaSection />

      <h4 className="text-lg font-semibold text-[#f0d060] mt-6">
        The Actual Numbers
      </h4>
      <p>
        The numbers below represent the benefits of various buildings. Keep in
        mind that
        <span className="text-green-400 font-semibold">
          {' '}
          racial and personality bonuses
        </span>{' '}
        may alter these values.
      </p>
      <DataTable>
        <thead>
          <tr>
            <TableHeader>Building</TableHeader>
            <TableHeader>Effects</TableHeader>
          </tr>
        </thead>
        <tbody>
          {buildings.map((building) => (
            <tr key={building.assetCode}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <AssetIcon
                    assetCode={building.assetCode.toLowerCase()}
                    assetType="building"
                    size="tiny"
                    width={30}
                    height={30}
                  />
                  <span>{building.name}</span>
                </div>
              </TableCell>
              <TableCell>Effects</TableCell>
            </tr>
          ))}
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/resource/barren_land.png"
                  alt="Barren Land"
                  width={30}
                  height={30}
                />
                <span>Barren lands</span>
              </div>
            </TableCell>
            <TableCell>
              Houses 15 people and produces 2 bushels per day
            </TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/homes.png"
                  alt="Homes"
                  width={30}
                  height={30}
                />
                <span>Homes</span>
              </div>
            </TableCell>
            <TableCell>
              Increases maximum population by 8<br />
              Increases birth rates by % * 3<br />
              <span className="text-yellow-300">Employs no one</span>
            </TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/farms.png"
                  alt="Farms"
                  width={30}
                  height={30}
                />
                <span>Farms</span>
              </div>
            </TableCell>
            <TableCell>Produces 70 bushels per day</TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/mills.png"
                  alt="Mills"
                  width={30}
                  height={30}
                />
                <span>Mills</span>
              </div>
            </TableCell>
            <TableCell>
              Decreases building costs by % * 4 (Max 99%)
              <br />
              Reduces exploration gold expenses by % * 3
            </TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/banks.png"
                  alt="Banks"
                  width={30}
                  height={30}
                />
                <span>Banks</span>
              </div>
            </TableCell>
            <TableCell>
              Generates $25 per day
              <br />
              Increases income by % * 1.25
            </TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/training_grounds.png"
                  alt="Training Grounds"
                  width={30}
                  height={30}
                />
                <span>Training Grounds</span>
              </div>
            </TableCell>
            <TableCell>
              Increases offensive military efficiency by % * 1.5
            </TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/armouries.png"
                  alt="Armouries"
                  width={30}
                  height={30}
                />
                <span>Armouries</span>
              </div>
            </TableCell>
            <TableCell>
              Decreases military training & draft costs by % * 1.5
              <br />
              Decreases daily wages by % * 2
            </TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/military_barracks.png"
                  alt="Barracks"
                  width={30}
                  height={30}
                />
                <span>Barracks</span>
              </div>
            </TableCell>
            <TableCell>Lowers attack time by % * 1.5</TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/forts.png"
                  alt="Forts"
                  width={30}
                  height={30}
                />
                <span>Forts</span>
              </div>
            </TableCell>
            <TableCell>
              Increases defensive military efficiency by % * 1.5
            </TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/guard_stations.png"
                  alt="Guard Stations"
                  width={30}
                  height={30}
                />
                <span>Guard Stations</span>
              </div>
            </TableCell>
            <TableCell>
              Decreases resource losses when attacked by % * 1.5
            </TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/hospitals.png"
                  alt="Hospitals"
                  width={30}
                  height={30}
                />
                <span>Hospitals</span>
              </div>
            </TableCell>
            <TableCell>
              % * 3 daily chance of curing the plague
              <br />
              Decreases military losses by % * 5 (Max 87.5%)
            </TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/guilds.png"
                  alt="Guilds"
                  width={30}
                  height={30}
                />
                <span>Guilds</span>
              </div>
            </TableCell>
            <TableCell>2% chance of training a wizard daily</TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/towers.png"
                  alt="Towers"
                  width={30}
                  height={30}
                />
                <span>Towers</span>
              </div>
            </TableCell>
            <TableCell>Produces 10 runes per day</TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/thieves_dens.png"
                  alt="Thieves Den"
                  width={30}
                  height={30}
                />
                <span>Thieves Den</span>
              </div>
            </TableCell>
            <TableCell>
              Lowers losses in thievery operations by % * 4<br />
              Increases thievery effectiveness by % * 3
            </TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/watchtowers.png"
                  alt="Watchtowers"
                  width={30}
                  height={30}
                />
                <span>Watchtowers</span>
              </div>
            </TableCell>
            <TableCell>
              % * 2.3 chance of catching enemy thieves
              <br />
              Decreases thievery damage by % * 3
            </TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/arcane_libraries.png"
                  alt="Arcane Libraries"
                  width={30}
                  height={30}
                />
                <span>Arcane Libraries</span>
              </div>
            </TableCell>
            <TableCell>
              % * 2.3 chance of stopping enemy spells
              <br />
              Decreases spell damage by % * 3
            </TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/stables.png"
                  alt="Stables"
                  width={30}
                  height={30}
                />
                <span>Stables</span>
              </div>
            </TableCell>
            <TableCell>
              Produces one horse per day (Max 60 horses per stable)
            </TableCell>
          </tr>
          <tr>
            <TableCell>
              <div className="flex items-center gap-2">
                <img
                  src="/images/assets/building/dungeons.png"
                  alt="Dungeons"
                  width={30}
                  height={30}
                />
                <span>Dungeons</span>
              </div>
            </TableCell>
            <TableCell>Houses 50 prisoners of war</TableCell>
          </tr>
        </tbody>
      </DataTable>
      <p className="text-yellow-300 italic mt-4">
        Building wisely is key to prosperity. Choose well, and your province
        will thrive.
      </p>
    </section>
  );
};

export const Route = createFileRoute('/economics/')({
  component: EconomicsPage,
});

function EconomicsPage() {
  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="max-w-6xl mx-auto space-y-8">
        <GuideMenu currentPath="/economics" />

        <section className="glass rounded-2xl p-6">
          {/* Header with icon */}
          <div className="flex items-center gap-4 mb-8">
            <img
              src="/images/misc/economics@2x.png"
              alt="Economics"
              width={80}
              height={80}
              className="shrink-0"
            />
            <h1 className="text-2xl font-bold text-[#e6c547]">Economics</h1>
          </div>

          {/* Content sections */}
          <div className="space-y-12 text-[#b8b8b8] leading-relaxed">
            <IntroSection />
            <EconomicBasicsSection />
            <OverpopulationSection />
            <NetWorthSection />
            <BuildingSection buildings={[]} />
          </div>
        </section>
      </div>
    </div>
  );
}
